import { mapState, mapGetters, mapActions } from 'vuex';

import { PROJECT_NAME } from '@config/application';
import serverUrl from '@config/server';
import generateNavigation from '@config/navigation';
import generateRouteInfo, { MAIN_ROUTE_NAME } from '@config/routeInformation';

import LocationSelector from '@components/locationSelector';

import NotificationsWidget from '@modules/notifications/_views/notificationsWidget';

import { decodeJWT } from '@helpers';

import { APP_SETTINGS_NAMESPACE, AppSettingsActionTypes } from '@store';

import { APP_NAMESPACE, GET_APP_SETTINGS } from '@store/modules/app.types';
import { AUTH_NAMESPACE, LOGOUT } from '@store/modules/auth.types';
import { LOCATIONS_NAMESPACE } from '@store/modules/locations.types';
import { ENUMS_NAMESPACE, GET_ENUMS } from '@store/modules/enums.types';

import { version as uiVersion } from '../../../package.json';

export default {
  name: 'AppWrapper',

  components: {
    LocationSelector,
    NotificationsWidget,
  },

  data() {
    return {
      refreshing: false,
      registration: null,
      refreshUIDialog: false,

      uiVersion,
      serverUrl,
      copyright: `© Opensoft - ${new Date().getFullYear()}`,
    };
  },

  computed: {
    ...mapState(AUTH_NAMESPACE, ['token', 'isAuthenticated']),
    ...mapState(LOCATIONS_NAMESPACE, ['suffix']),
    ...mapState(APP_NAMESPACE, ['apiVersion']),
    ...mapGetters(APP_NAMESPACE, ['userName']),

    projectTitle() {
      return {
        name: PROJECT_NAME,
        to: {
          name: 'dashboard',
        },
      };
    },

    needMainApp() {
      const { name } = this.$route;
      return name !== 'error404' && name !== 'forbidden' && name !== 'login' && this.token;
    },

    navigationConfig() {
      return generateNavigation(this.$router.options.routes, this.$route);
    },

    routeInfoConfig() {
      const { routes = [] } = this.$router.options;
      const MAIN_ROUTE = routes.find(route => route.name === MAIN_ROUTE_NAME) || {};
      const MAIN_ROUTE_CONFIG = {
        name: MAIN_ROUTE_NAME,
        title: MAIN_ROUTE.meta.title,
        to: MAIN_ROUTE.meta.navigation.to,
      };

      return this.$route.name === 'dashboard'
        ? undefined
        : generateRouteInfo(this.$route, MAIN_ROUTE_CONFIG);
    },

    bugReportInfo() {
      const { name, email } = decodeJWT(this.token);
      return {
        projectName: PROJECT_NAME,
        uiVersion: this.uiVersion,
        apiVersion: this.apiVersion,
        url: window.location.href,
        username: name,
        email,
      };
    },
  },

  watch: {
    suffix(suffix) {
      this.getAppDigitalSettings({ suffix });
    },
  },

  created() {
    this.initApp();

    document.addEventListener('swUpdated', this.showRefreshUI);

    this.addUIServiceWorkerTrigger();
  },

  beforeDestroy() {
    document.removeEventListener('swUpdated', this.showRefreshUI);
  },

  methods: {
    ...mapActions(APP_NAMESPACE, {
      getAppSettings: GET_APP_SETTINGS,
    }),
    ...mapActions(APP_SETTINGS_NAMESPACE, {
      getAppDigitalSettings: AppSettingsActionTypes.GET_APP_DIGITAL_SETTINGS,
    }),
    ...mapActions(ENUMS_NAMESPACE, {
      getEnums: GET_ENUMS,
    }),
    ...mapActions(AUTH_NAMESPACE, {
      onLogout(dispatch) {
        dispatch(LOGOUT, { routeName: this.$route.name });
        this.$router.push({ name: 'login' });
      },
    }),

    initApp() {
      this.getAppSettings();

      if (this.isAuthenticated) {
        this.getAppData();
      }
    },

    getAppData() {
      this.getAppDigitalSettings();
      this.getEnums();
    },

    showRefreshUI({ detail }) {
      this.registration = detail;
      this.refreshUIDialog = true;
    },

    closeRefreshUI() {
      this.refreshUIDialog = false;
      this.registration = null;
    },

    refreshApp() {
      this.refreshUIDialog = false;

      if (!this.registration || !this.registration.waiting) {
        return;
      }

      this.registration.waiting.postMessage('SKIP_WAITING');
    },

    addUIServiceWorkerTrigger() {
      if (navigator.serviceWorker && !this.refreshing) {
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (this.refreshing) {
            return;
          }

          this.refreshing = true;

          window.location.reload();
        });
      }
    },
  },
};
